/* eslint-disable no-underscore-dangle */

import {
  put,
  fork,
  takeLatest,
  all,
  select,
  call,
  delay,
} from "redux-saga/effects";
import { logging, capitalizer } from "root/libs/core-libs/src";
import qs from "qs";
import http from "one-time-shipment/src/lib/http";
import {
  Localization,
  CanaryVersions,
  MicroFrontend,
} from "root/libs/ui-containers/src";
import {
  selectAbTestRunning,
  selectAbTestVersion,
} from "root/libs/ui-containers/src/lib/AbTest";
import { P2PLinkTypes } from "root/libs/constants/src";
import { applyDefaultPurchaseMethod } from "root/libs/core-libs/src/lib/offers/applyDefaultPurchaseMethod";
import merge from "lodash/merge";
import get from "lodash/get";
import isNil from "lodash/isNil";
import { toggleModalError } from "one-time-shipment/src/containers/OffersLane/actions";
import { selectCountrySettings } from "root/libs/ui-containers/src/lib/CountrySettings/selectors";
import {
  getOffersRequestSucceeded as getOffersRequestSucceededAction,
  getOffersRequestFailed as getOffersRequestFailedAction,
  getOffersRequestSent as getOffersRequestSentAction,
} from "./actions";

import {
  selectTradeLaneInitialSet,
  selectDocumentsOptionValue,
} from "../TradeLane/selectors";
import { makeShipmentLaneInitialSetSelection } from "../ShipmentLane/selectors";

import { GET_OFFERS_REQUEST_SENT, UPDATE_SHIPPING_DATE } from "./constants";
import { CATEGORIES, LAYOUT_FLAVOR } from "../SpotShipmentPage/constants";

const {
  selectors: { selectCanaryVersion },
} = CanaryVersions;

const {
  selectors: { selectMicroFrontendId },
} = MicroFrontend;

const {
  selectors: { makeSelectLocale, selectCountry },
} = Localization;

const getFreightSet = ({
  shipmentLaneInitialSet,
  countryPresetSizes,
  shipmentLane,
}) => {
  const freightItems = shipmentLaneInitialSet.items.map(
    ({ preset, ...item }) => {
      let selectedPreset;
      if (preset) {
        selectedPreset = countryPresetSizes.find((p) => p.key === preset);
        // if selected category is not set to pallet we assume it should stay undefined,
        //  everything else should be a parcel
      } else if (shipmentLane.selectedCategory !== CATEGORIES.Pallet) {
        selectedPreset = countryPresetSizes.find(
          (p) =>
            p.category === (shipmentLane.selectedCategory ?? CATEGORIES.Parcel)
        );
      }
      return {
        ...item,
        presetSize: selectedPreset?.presetSize,
        // TODO: load from countrySettings
        packageCode: selectedPreset?.packageCode ?? "CT",
      };
    }
  );
  return { ...shipmentLaneInitialSet, items: freightItems };
};
/**
 * Get city details from the API
 * @param {Object} params - The parameters for the API call
 * @param {string} params.countryCode - The country code
 * @param {string} params.city - The city
 * @param {string} params.postalCode - The postal code
 * @returns {Object} The city details
 */
function* getCityDetails(params) {
  const response = yield call(http.getPostalLocationQuery, params);
  const cityDetails = (response?.data?.postalLocationList ?? []).find(
    (data) =>
      (data.cityLocalized || data.city).toUpperCase() ===
        params.city.toUpperCase() &&
      data.countryCode?.toUpperCase() === params.countryCode?.toUpperCase() &&
      data.postalCode?.toUpperCase() === params.postalCode?.toUpperCase()
  );
  return cityDetails;
}

export function* getOffersRequestSent() {
  try {
    const spotShipmentPage = yield select((state) => state.spotShipmentPage);
    const shipmentLane = yield select((state) => state.shipmentLane);
    const currentSegment = spotShipmentPage.selectedSegment;
    const tradeLaneInitialSet = yield select(selectTradeLaneInitialSet);
    const origin = yield call(getCityDetails, {
      countryCode: tradeLaneInitialSet.originCountry,
      city: tradeLaneInitialSet.originCity,
      postalCode: tradeLaneInitialSet.originZip,
    });
    const destination = yield call(getCityDetails, {
      countryCode: tradeLaneInitialSet.destinationCountry,
      city: tradeLaneInitialSet.destinationCity,
      postalCode: tradeLaneInitialSet.destinationZip,
    });
    const tradeLaneCityLocalized = {
      originCity: capitalizer(origin?.city ?? tradeLaneInitialSet.originCity),
      destinationCity: capitalizer(
        destination?.city ?? tradeLaneInitialSet.destinationCity
      ),
    };

    // get country ots data
    const { data: countryOtsData } = yield select(selectCountrySettings);
    const shipmentLaneInitialSet = yield select(
      makeShipmentLaneInitialSetSelection(spotShipmentPage.selectedSegment)()
    );
    const countryPresetSizes =
      countryOtsData.spotShipmentPresetSizes[currentSegment];

    const language = yield select(makeSelectLocale());
    const locale = { language: language.substring(0, 2) };
    const pageCountry = yield select(selectCountry);
    const marketCountry = { marketCountry: pageCountry };
    const abTestRunning = yield select(selectAbTestRunning);
    const abTestVersion = abTestRunning && {
      abTestVersion: yield select(selectAbTestVersion),
    };
    const canaryVersionSelector = yield select(selectCanaryVersion);
    const microFrontendIdSelector = yield select(selectMicroFrontendId);

    const initialSetMerged = merge(
      {},
      tradeLaneInitialSet,
      tradeLaneCityLocalized,
      countryOtsData.layoutFlavor === LAYOUT_FLAVOR.FREIGHT
        ? getFreightSet({
            shipmentLaneInitialSet,
            countryPresetSizes,
            shipmentLane,
          })
        : shipmentLaneInitialSet,
      locale,
      marketCountry,
      abTestVersion,
      spotShipmentPage
    );

    const { isShippingDateVisible, shippingDate } = yield select(
      (state) => state.getQuote
    );

    const params = window.location.search;
    const isDemoMode = qs.parse(params.slice(1)).demo;

    const options = {};

    const documentsOption = yield select(selectDocumentsOptionValue);

    if (!isNil(documentsOption)) {
      options["option.DOCUMENTS"] = documentsOption[currentSegment];
    }

    // When there's no Canary version selected, we shouldn't append the header
    let canaryParam = {};
    if (!isNil(canaryVersionSelector) && canaryVersionSelector !== "") {
      canaryParam = { canaryVersion: canaryVersionSelector };
    }

    let microFrontendParam = {};
    if (!isNil(microFrontendIdSelector) && microFrontendIdSelector !== "") {
      microFrontendParam = { microFrontendId: microFrontendIdSelector };
    }

    if (isDemoMode === "true") {
      options.demo = true;
    }

    if (isShippingDateVisible) {
      options["option.SHIPPING_DATE"] = shippingDate;
    }

    // Get Adobe Target Experience value
    const abTestEnv =
      window._satellite?.getVar("ADOBE TARGET EXPERIENCES") || "";
    // Trim the value to 500 characters to prevent quote api failures
    const trimmedAbTestEnv = abTestEnv ? abTestEnv.substring(0, 500) : "";

    options.abTestEnv = trimmedAbTestEnv;

    yield delay(100);
    const response = yield call(http.get, "quote", {
      params: { ...initialSetMerged, ...options },
      adapter: "quote",
      ...canaryParam,
      ...microFrontendParam,
    });

    const offersArray = get(response, "data.offers", []);

    offersArray.forEach((obj, index) => {
      if (obj.pathToPurchaseLinkType === P2PLinkTypes.MODAL_FREIGHT) {
        response.data.offers[index].freightModal = {
          quotationId: obj.quotationId,
          amount: get(obj, "price.amount", 0),
          tax: get(obj, "price.tax", ""),
          currencyCode: get(obj, "price.currencyCode", ""),
          deliveryOption: obj.deliveryOption,
          deliveryType: obj.deliveryType,
          originCountry: tradeLaneInitialSet.originCountry,
          pickUpDate: obj.pickUpDate,
          destinationCountry: tradeLaneInitialSet.destinationCountry,
          deliveryDate: obj.estimatedDeliveryTime,
        };
      }
    });

    response.data.offers = applyDefaultPurchaseMethod(offersArray);

    yield put(getOffersRequestSucceededAction(response.data));

    const { metadata, backends } = response.data;

    yield call(logging.logMetadata, metadata);
    yield call(logging.logBackends, backends);
  } catch (error) {
    logging.warn(error);
    yield put(toggleModalError());
    yield put(
      getOffersRequestFailedAction({
        error,
      })
    );
  }
}

/**
 * Watch for documents info update and trigger get offers requests every once in 300ms
 */
export function* updateOffers() {
  yield delay(300);
  const { isOffersLaneVisible } = yield select((state) => state.offersLane);

  /*
    Check if offers lane is visible and triger Offers request if true.
    If not, do not trigger offers request, as the shipment lane may not be valid yet
   */
  if (isOffersLaneVisible) {
    yield put(getOffersRequestSentAction());
  }
}

export function* watchFetch() {
  yield takeLatest(GET_OFFERS_REQUEST_SENT, getOffersRequestSent);
}

export function* watchShippingDate() {
  yield takeLatest(UPDATE_SHIPPING_DATE, updateOffers);
}

export default function* sagas() {
  yield all([fork(watchFetch), fork(watchShippingDate)]);
}
